// src/components/AddApiKeys.tsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import {
    TextField,
    Button,
    Container,
    Typography,
    Box,
    Paper,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ApiKeysContext } from '../contexts/ApiKeysContext';

const AddApiKeys: React.FC = () => {
    const [tavilyApiKey, setTavilyApiKey] = useState<string>('');
    const [openaiApiKey, setOpenaiApiKey] = useState<string>('');
    const [alphavantageApiKey, setAlphavantageApiKey] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const navigate = useNavigate();

    // Create a ref for the API Keys section
    const apiKeysSectionRef = useRef<HTMLDivElement>(null);

    // New state variables
    const [isApiKeysSet, setIsApiKeysSet] = useState<boolean>(false);
    const [isPaidUser, setIsPaidUser] = useState<boolean>(false);
    const [isSubscriber, setIsSubscriber] = useState<boolean>(false);
    const [isUsingFree, setIsUsingFree] = useState<boolean>(false);
    const [subscriptionCreated, setSubscriptionCreated] = useState<string>('');
    const [subscriptionPeriodStart, setSubscriptionPeriodStart] = useState<string>('');
    const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] = useState<string>('');
    const [chatAgentUsed, setChatAgentUsed] = useState<number>(0);
    const [chatAgentLimit, setChatAgentLimit] = useState<number>(0);
    const [financialAgentUsed, setFinancialAgentUsed] = useState<number>(0);
    const [financialAgentLimit, setFinancialAgentLimit] = useState<number>(0);

    useEffect(() => {
        const fetchUserData = async () => {
            setIsLoading(true);
            try {
                const response = await api.get('/user-usage/usage');
                const userData = response.data;
                setIsApiKeysSet(userData.is_api_keys_set || false);
                setIsPaidUser(userData.is_paid_user || false);
                setIsSubscriber(userData.is_subscriber || false);
                setIsUsingFree(userData.is_using_free || false);
                setSubscriptionCreated(userData.subscription_created || '');
                setSubscriptionPeriodStart(userData.subscription_period_start || '');
                setSubscriptionPeriodEnd(userData.subscription_period_end || '');
                setChatAgentUsed(userData.chat_agent_used || 0);
                setChatAgentLimit(userData.chat_agent_limit || 0);
                setFinancialAgentUsed(userData.financial_agent_used || 0);
                setFinancialAgentLimit(userData.financial_agent_limit || 0);

                setTavilyApiKey(userData.tavily_api_key || '');
                setOpenaiApiKey(userData.openai_api_key || '');
                setAlphavantageApiKey(userData.alpha_vantage_api_key || '');
            } catch (error) {
                setErrorMessage('Failed to fetch user data. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchUserData();
    }, []);

    const { refreshApiKeysStatus } = useContext(ApiKeysContext);

    const handleAddApiKeys = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsUpdating(true);
        try {
            const apiKeyPayload = {
                tavily_api_key: tavilyApiKey,
                openai_api_key: openaiApiKey,
                alpha_vantage_api_key: alphavantageApiKey,
            };

            await api.patch('/user-usage/add-api-keys', apiKeyPayload);
            setSuccessMessage('API keys updated successfully.');
            await refreshApiKeysStatus();
        } catch (error) {
            setErrorMessage('Failed to update API keys. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePayment = async (paymentType: 'subscription' | 'one_off') => {
        setIsUpdating(true);
        try {
            const response = await api.post(`/user-usage/create-payment?payment_type=${paymentType}`);
            const { url } = response.data;
            window.location.href = url; // Redirect to Stripe checkout
        } catch (error) {
            setErrorMessage('Failed to initiate payment. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    // Function to format dates
    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    if (isLoading) {
        return (
            <Container maxWidth="sm">
                <Box mt={8} display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    const paymentType = isSubscriber ? 'Subscription' : 'Top-Up';

    return (
        <Container maxWidth="sm">
            <Box mt={8} component={Paper} elevation={3} p={4} borderRadius="12px">
                {/* Upgrade Options */}
                <Typography
                    variant="h4"
                    gutterBottom
                    align="center"
                    sx={{
                        fontWeight: 600,
                        color: '#0D47A1',
                    }}
                >
                    Upgrade Options
                </Typography>

                {/* Subscription Option */}
                <Box mt={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Subscription - $35/month
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                            Unlimited access to Financial Chat Agent and Financial Analyst Agent.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isUpdating}
                            sx={{
                                mt: 2,
                                fontWeight: 600,
                                padding: '0.5rem',
                                fontSize: '1rem',
                                borderRadius: '20px',
                                textTransform: 'none',
                            }}
                            onClick={() => handlePayment('subscription')}
                        >
                            {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Subscribe Now'}
                        </Button>
                    </Paper>
                </Box>

                {/* One-Time Top Up Option */}
                <Box mt={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            One-Time Top Up - $10
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                            Get 100 chat AI messages and 20 financial analyst reports.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isUpdating}
                            sx={{
                                mt: 2,
                                fontWeight: 600,
                                padding: '0.5rem',
                                fontSize: '1rem',
                                borderRadius: '20px',
                                textTransform: 'none',
                            }}
                            onClick={() => handlePayment('one_off')}
                        >
                            {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Buy Now'}
                        </Button>
                    </Paper>
                </Box>

                {/* Divider */}
                <Box mt={4} mb={2}>
                    <Divider />
                </Box>

                {isApiKeysSet && (isPaidUser || isSubscriber) ? (
                    // Render usage information
                    <>
                        <Typography
                            variant="h4"
                            gutterBottom
                            align="center"
                            sx={{
                                fontWeight: 600,
                                color: '#0D47A1',
                            }}
                        >
                            Your Usage
                        </Typography>

                        <Box mt={2}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                                <Typography variant="body1" sx={{ mt: 1 }}>
                                    <strong>Type:</strong> {paymentType}
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 1 }}>
                                    <strong>Chat:</strong> {chatAgentUsed} / {chatAgentLimit}
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 1 }}>
                                    <strong>Report:</strong> {financialAgentUsed} / {financialAgentLimit}
                                </Typography>

                                {isSubscriber && (
                                    <>
                                        <Typography variant="body1" sx={{ mt: 1 }}>
                                            <strong>Subscription Created:</strong> {formatDate(subscriptionCreated)}
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 1 }}>
                                            <strong>Subscription Period Start:</strong> {formatDate(subscriptionPeriodStart)}
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 1 }}>
                                            <strong>Subscription Period End:</strong> {formatDate(subscriptionPeriodEnd)}
                                        </Typography>
                                    </>
                                )}
                            </Paper>
                        </Box>
                    </>
                ) : (
                    // Render the API keys section
                    <>
                        <Typography
                            id="api-keys-section"
                            ref={apiKeysSectionRef}
                            variant="h4"
                            gutterBottom
                            align="center"
                            sx={{
                                fontWeight: 600,
                                color: '#0D47A1',
                            }}
                        >
                            Manage Your API Keys
                        </Typography>
                        {errorMessage && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Alert>
                        )}
                        {successMessage && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {successMessage}
                            </Alert>
                        )}
                        <form onSubmit={handleAddApiKeys}>
                            {/* Tavily API Key */}
                            <Box mt={2}>
                                <TextField
                                    label="Tavily API Key"
                                    required={true}
                                    variant="outlined"
                                    fullWidth
                                    value={tavilyApiKey}
                                    onChange={(e) => setTavilyApiKey(e.target.value)}
                                    disabled={isUpdating}
                                    InputProps={{
                                        style: {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />
                            </Box>

                            {/* OpenAI API Key */}
                            <Box mt={2}>
                                <TextField
                                    label="OpenAI API Key"
                                    required={true}
                                    variant="outlined"
                                    fullWidth
                                    value={openaiApiKey}
                                    onChange={(e) => setOpenaiApiKey(e.target.value)}
                                    disabled={isUpdating}
                                    InputProps={{
                                        style: {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />
                            </Box>

                            {/* Alpha Vantage API Key */}
                            <Box mt={2}>
                                <TextField
                                    label="Alpha Vantage API Key (Premium)"
                                    required={true}
                                    variant="outlined"
                                    fullWidth
                                    value={alphavantageApiKey}
                                    onChange={(e) => setAlphavantageApiKey(e.target.value)}
                                    disabled={isUpdating}
                                    InputProps={{
                                        style: {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />
                            </Box>

                            <Box mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={isUpdating}
                                    sx={{
                                        fontWeight: 600,
                                        padding: '0.75rem',
                                        fontSize: '1rem',
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                                    }}
                                >
                                    {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Update API Keys'}
                                </Button>
                            </Box>
                        </form>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default AddApiKeys;
